import React, { useEffect, useState, useRef } from "react";
import { Spinner, Alert } from "react-bootstrap";
import {
  playChatNotificationSound,
  requestNotificationPermission,
  showBrowserNotification,
} from "../../utils/notificationSound";
import { isAppVisible } from "../../utils/notificationManager";

/**
 * ZoomMeetingEmbed — Renders the Zoom Meeting SDK (Client View) inside #zmmtg-root.
 *
 * IMPORTANT: This component expects `zoomConfig` to be available on mount.
 * Parent components must gate rendering behind a loading state so that
 * zoomConfig is never null when this component first mounts.
 *
 * The Zoom Meeting SDK manages global DOM state (#zmmtg-root, WASM, event
 * listeners). For reliable initialisation the classroom routes use
 * window.location.href (full-page navigation) instead of React-Router
 * navigate(), giving the SDK a clean page on every entry.
 */
const ZoomMeetingEmbed = ({ zoomConfig, onMeetingEnd, leaveUrl, notifyOnBackgroundChat = false, autoPictureInPicture = false }) => {
  const [zoomStatus, setZoomStatus] = useState("loading"); // "loading" | "in-meeting" | "error"
  const [errorMsg, setErrorMsg] = useState("");
  const isMountedRef = useRef(true);
  const zoomMtgRef = useRef(null);
  const onMeetingEndRef = useRef(onMeetingEnd);
  onMeetingEndRef.current = onMeetingEnd;
  const leaveUrlRef = useRef(leaveUrl);
  leaveUrlRef.current = leaveUrl;

  // ── Background chat-alert bookkeeping (trainer view only) ──
  // When the LMS tab is backgrounded, an incoming Zoom chat message flashes the
  // browser tab title; this is cleared when the trainer returns to the tab.
  const titleFlashIntervalRef = useRef(null);
  const originalTitleRef = useRef(null);

  const stopTitleFlash = () => {
    if (titleFlashIntervalRef.current) {
      clearInterval(titleFlashIntervalRef.current);
      titleFlashIntervalRef.current = null;
    }
    if (originalTitleRef.current !== null) {
      document.title = originalTitleRef.current;
    }
  };

  const startTitleFlash = () => {
    if (titleFlashIntervalRef.current) return; // already flashing
    if (originalTitleRef.current === null) originalTitleRef.current = document.title;
    let toggled = false;
    titleFlashIntervalRef.current = setInterval(() => {
      document.title = toggled ? originalTitleRef.current : "💬 New message…";
      toggled = !toggled;
    }, 1000);
  };
  // Ensure onMeetingEnd is only invoked once — the SDK can re-emit ended/disconnected
  // events multiple times (e.g. on cleanup or when the host ends + the leave fires).
  const meetingEndedRef = useRef(false);
  const pipHandlerSetRef = useRef(false);
  const fireMeetingEnded = () => {
    if (meetingEndedRef.current) return;
    meetingEndedRef.current = true;
    if (onMeetingEndRef.current) onMeetingEndRef.current();
  };

  useEffect(() => {
    isMountedRef.current = true;

    /* ── Prepare a FRESH #zmmtg-root ── */
    const old = document.getElementById("zmmtg-root");
    if (old) old.remove(); // remove stale div + any leftover SDK content

    const zmDiv = document.createElement("div");
    zmDiv.id = "zmmtg-root";
    zmDiv.style.display = "block"; // override the CSS display:none rule
    document.body.appendChild(zmDiv);

    /* ── Stop the chat title-flash once the trainer returns to the tab ── */
    const handleVisibilityRestore = () => {
      if (isAppVisible()) stopTitleFlash();
    };
    if (notifyOnBackgroundChat) {
      document.addEventListener("visibilitychange", handleVisibilityRestore);
      window.addEventListener("focus", handleVisibilityRestore);
    }

    /* ── Main initialisation ── */
    const joinMeeting = async () => {
      // Config validation
      if (
        !zoomConfig ||
        !zoomConfig.meetingId ||
        !zoomConfig.signature ||
        !zoomConfig.sdkKey ||
        !zoomConfig.userName
      ) {
        if (isMountedRef.current) {
          setErrorMsg("Zoom configuration missing or invalid.");
          setZoomStatus("error");
        }
        return;
      }

      setZoomStatus("loading");

      try {
        console.log('[ZoomSDK] Config received:', {
          meetingId: zoomConfig.meetingId,
          sdkKey: zoomConfig.sdkKey ? zoomConfig.sdkKey.substring(0, 6) + '...' : 'MISSING',
          hasSignature: !!zoomConfig.signature,
          hasZak: !!zoomConfig.zakToken,
          userName: zoomConfig.userName,
          zoomMode: zoomConfig.zoomMode,
        });

        // Dynamically import the SDK (cached after first load)
        const { ZoomMtg } = await import("@zoom/meetingsdk");
        if (!isMountedRef.current) return;

        zoomMtgRef.current = ZoomMtg;
        console.log('[ZoomSDK] SDK loaded successfully');

        // Pre-load WASM & prepare web resources
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();

        // Wait for SDK assets to be ready
        await new Promise((resolve) => setTimeout(resolve, 2000));
        if (!isMountedRef.current) return;

        // Re-ensure #zmmtg-root is visible (SDK may have toggled it)
        const rootDiv = document.getElementById("zmmtg-root");
        if (rootDiv) rootDiv.style.display = "block";

        console.log('[ZoomSDK] Calling init...');
        ZoomMtg.init({
          leaveUrl: leaveUrlRef.current || window.location.origin,
          patchJsMedia: true,
          // leaveOnPageUnload disabled: it makes the SDK fight our React-driven
          // navigation when the meeting ends, which can trap the user on the
          // Zoom "meeting disconnected" overlay (page keeps reloading).
          leaveOnPageUnload: false,
          disablePreview: true,
          success: () => {
            if (!isMountedRef.current) return;
            console.log('[ZoomSDK] Init success, joining meeting:', String(zoomConfig.meetingId));
            console.log('[ZoomSDK] ZAK token present:', !!zoomConfig.zakToken, 'length:', (zoomConfig.zakToken || '').length);
            ZoomMtg.join({
              signature: zoomConfig.signature,
              sdkKey: zoomConfig.sdkKey,
              meetingNumber: String(zoomConfig.meetingId),
              passWord: zoomConfig.passcode || "",
              userName: zoomConfig.userName,
              userEmail: zoomConfig.userEmail || "",
              tk: zoomConfig.registrantToken || "",
              zak: zoomConfig.zakToken || "",
              success: () => {
                console.log('[ZoomSDK] JOIN SUCCESS — meeting is embedded');
                if (isMountedRef.current) setZoomStatus("in-meeting");

                // Listen for meeting end inside the SDK
                ZoomMtg.inMeetingServiceListener('onMeetingStatus', function (data) {
                  console.log('[ZoomSDK] Meeting status change:', data.meetingStatus);
                  // Zoom Meeting SDK status codes:
                  //   1 = Connecting, 2 = InMeeting, 3 = Disconnected,
                  //   4 = Reconnecting (NETWORK BLIP — do NOT treat as ended),
                  //   5 = Failed, 6 = Ended (host ended)
                  // Only 3 and 6 mean the meeting is actually over.
                  if (data.meetingStatus === 3 || data.meetingStatus === 6) {
                    fireMeetingEnded();
                  }
                });

                // Blur and virtual backgrounds sit in Settings → Background,
                // which Zoom shows only when this browser can run them AND the
                // host's Zoom account has "Virtual background" switched on.
                ZoomMtg.isSupportVirtualBackground({
                  success: (data) => console.log('[ZoomSDK] Virtual background support:', data?.result),
                  error: (err) => console.warn('[ZoomSDK] Virtual background unavailable:', err),
                });

                // Trainer view: float the meeting when the trainer switches tabs,
                // as Google Meet does. Chrome 120+ calls this handler on a tab
                // switch while the page is using the mic or camera, and lets it
                // open a picture-in-picture window without a click; Chrome closes
                // it again on return. Zoom's own PiP button (top-right of the
                // meeting) does the rest — it is Zoom's markup, so an SDK update
                // that renames it makes this do nothing, never break.
                if (autoPictureInPicture) {
                  try {
                    navigator.mediaSession.setActionHandler('enterpictureinpicture', () => {
                      const pipButton = document.querySelector('.pip-button[aria-label="Enter Pip"]');
                      if (pipButton) pipButton.click();
                    });
                    pipHandlerSetRef.current = true;
                  } catch (_) {
                    /* browser without automatic picture-in-picture */
                  }
                  ZoomMtg.inMeetingServiceListener('onPictureInPicture', function (data) {
                    console.log('[ZoomSDK] Picture-in-picture:', data);
                  });
                }

                // Trainer view: alert on incoming chat while the LMS tab is backgrounded.
                if (notifyOnBackgroundChat) {
                  // Best-effort permission request; sound + title-flash work even if denied.
                  requestNotificationPermission().catch(() => {});

                  ZoomMtg.inMeetingServiceListener('onReceiveChatMsg', function (data) {
                    try {
                      // Payload shape varies by SDK build — read fields defensively.
                      const senderName =
                        (data && (data.sender?.name || data.senderName || data.userName)) || 'Student';
                      const text = (data && (data.message || data.content || data.text)) || 'New chat message';

                      // Ignore the trainer's own outgoing messages.
                      if (zoomConfig.userName && senderName === zoomConfig.userName) return;

                      // Foreground: Zoom's own chat UI already shows the message.
                      if (isAppVisible()) return;

                      playChatNotificationSound();
                      const notif = showBrowserNotification(`💬 ${senderName}`, {
                        body: text,
                        tag: 'zoom-chat',
                        renotify: true,
                      });
                      if (notif) {
                        notif.onclick = () => {
                          window.focus();
                          notif.close();
                        };
                      }
                      startTitleFlash();
                    } catch (e) {
                      console.warn('[ZoomSDK] chat notify failed:', e);
                    }
                  });
                }
              },
              error: (err) => {
                console.error('[ZoomSDK] JOIN ERROR:', err);
                if (isMountedRef.current) {
                  setErrorMsg(
                    "Failed to join Zoom meeting: " +
                      (err.reason || err.errorMessage || JSON.stringify(err))
                  );
                  setZoomStatus("error");
                }
              },
            });
          },
          error: (err) => {
            console.error('[ZoomSDK] INIT ERROR:', err);
            if (isMountedRef.current) {
              setErrorMsg(
                "Zoom initialization failed: " +
                  (err.reason || err.errorMessage || JSON.stringify(err))
              );
              setZoomStatus("error");
            }
          },
        });
      } catch (err) {
        if (isMountedRef.current) {
          setErrorMsg("Failed to load Zoom SDK: " + (err.message || String(err)));
          setZoomStatus("error");
        }
      }
    };

    joinMeeting();

    /* ── Cleanup ── */
    return () => {
      isMountedRef.current = false;

      // Stop any chat title-flash and detach its visibility listeners.
      stopTitleFlash();
      if (notifyOnBackgroundChat) {
        document.removeEventListener("visibilitychange", handleVisibilityRestore);
        window.removeEventListener("focus", handleVisibilityRestore);
      }

      if (pipHandlerSetRef.current) {
        try {
          navigator.mediaSession.setActionHandler('enterpictureinpicture', null);
        } catch (_) {
          /* nothing registered */
        }
      }

      // Attempt SDK-level teardown
      try {
        if (zoomMtgRef.current) zoomMtgRef.current.destroy();
      } catch (_) {
        /* SDK may not support destroy — safe to ignore */
      }

      // Hide the root container
      const zmmtgRoot = document.getElementById("zmmtg-root");
      if (zmmtgRoot) zmmtgRoot.style.display = "none";

      // Remove any Zoom modal overlays so other routes stay clickable
      document
        .querySelectorAll(".ReactModal__Overlay, .ReactModalPortal")
        .forEach((el) => {
          if (el.parentNode) el.parentNode.removeChild(el);
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — parent gates rendering until config is ready

  return (
    <div>
      {zoomStatus === "loading" && (
        <div className="text-center my-4">
          <Spinner />
          <div>Loading Zoom Meeting...</div>
        </div>
      )}
      {zoomStatus === "error" && (
        <Alert variant="danger" className="my-4">
          {errorMsg || "Unable to load Zoom meeting."}
        </Alert>
      )}
      {/* Zoom SDK renders inside #zmmtg-root at body level */}
    </div>
  );
};

export default ZoomMeetingEmbed;
